import { useEffect } from 'react';

import { ACCESS_CODE_PARAM, buildLti13Endpoints, buildManualPlatformEndpoints, ELms, ELtiVersion, GMETRIX_LTI_LAUNCH_URL, GMETRIX_LTI_SETUP_OVERVIEW_URL, LMS_LABELS, LMS_LTI13_CLIENT_IDS, RESOURCE_KEY_PARAM } from 'pages/integrations/components/lti/constants';
import type { ResourceSelection } from 'pages/lti_resource_keys/components/ResourceKeyBrowser/ResourceKeyBrowser';
import { ToolEndpointFields } from 'pages/integrations/components/ToolEndpointFields/ToolEndpointFields';
import { buildCustomParams, formatCustomParamsBlock } from 'pages/integrations/components/lti/payload';
import { AccessCodeLookupModal } from 'pages/integrations/components/lti/AccessCodeLookupModal';
import { SetupStepsLinks } from 'pages/integrations/components/SetupStepsLinks/SetupStepsLinks';
import { ResourcePickerModal } from 'pages/integrations/components/lti/ResourcePickerModal';
import { CopyField } from 'pages/integrations/components/CopyField/CopyField';
import { Accordion, Card, Col, Form, InputGroup, Row } from 'react-bootstrap';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { ConsumerSettings, PartnerApi } from 'apis/PartnerApi';
import { FormattedMessage, useIntl } from 'react-intl';
import { helpContent } from 'pages/integrations/help';
import { useNotification } from 'app/notifications';
import { PageHeader } from 'components/layout/page';
import { DefaultLayout } from 'components/layout';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

import 'pages/integrations/styles/lti-integrations.scss';

// Schoology has not yet approved GMetrix for their App Center, so the panel below is hidden: it
// would otherwise advertise an install path no customer can complete. Flip to true once the
// listing goes live (AB#41173). This is a render guard rather than commented-out JSX so the
// panel's react-intl messages stay in the extraction and keep getting translated while hidden.
const SHOW_SCHOOLOGY_APP_CENTER_PANEL = false;

// Awaits a request but converts failure into a logged fallback, so the page's parallel loads
// are isolated — one failing endpoint must not blank the others' data.
const loadQuietly = async <T, >(request: Promise<T>, fallback: T, label: string): Promise<T> => {

    try {

        return await request;

    } catch (error: any) {

        console.error(`Failed to load ${label}:`, error);
        return fallback;

    }

};

export const Integrations = () => {

    const { highestRole, selectedPartner } = useApp();
    const { formatMessage } = useIntl();
    const { createToast } = useNotification();

    // The page always operates on the partner currently being managed; fall back to the user's
    // own highest-role partner when no managed partner is selected.
    const partnerId = selectedPartner?.id ?? highestRole.partnerId;

    const [state, setState] = useSuperState({
        accessCode: '',
        blackboardDeploymentId: '',
        consumerSettings: undefined as ConsumerSettings | undefined,
        generatingCredentials: false,
        loading: true,
        manualAccessTokenUrl: '',
        manualAuthorizeUrl: '',
        manualClientId: '',
        manualDeploymentId: '',
        manualIssuer: '',
        manualJwkSetUrl: '',
        manualLms: ELms.CANVAS as ELms.CANVAS | ELms.MOODLE,
        manualPlatformUrl: '',
        resourceKey: '',
        savingBlackboardDeploymentId: false,
        savingManualPlatform: false,
        savingSchoologyDeploymentId: false,
        schoologyDeploymentId: '',
        selectedResource: null as ResourceSelection | null,
        showAccessCodeModal: false,
        showResourceModal: false
    });

    const fetchSettings = async () => {

        setState({ loading: true });

        // Only the tool-side values (the dynamic-registration URL and the copyable tool endpoints)
        // are loaded here. Every manual-registration form deliberately starts blank each time and
        // never surfaces a partner's previously-saved entries.
        const settings = await loadQuietly(PartnerApi.GetConsumerSettingsByPartnerId(partnerId), undefined as ConsumerSettings | undefined, 'LTI consumer settings');

        setState({ consumerSettings: settings, loading: false });

    };

    useEffect(() => {

        fetchSettings();

    }, [partnerId]);

    const copyClipboardWithMessage = async (text: string) => {

        try {

            await navigator.clipboard.writeText(text);
            createToast(formatMessage({ id: 'copied_to_clipboard.65c86f91', defaultMessage: 'Copied to clipboard!' }), 'success');

        } catch (e) {

            createToast(formatMessage({ id: 'failed_to_copy_to_clipboard.9c57abcd', defaultMessage: 'Failed to copy to clipboard.' }), 'danger');

        }

    };

    const generateLTIKeyAndSecret = async () => {

        try {

            setState({ generatingCredentials: true });

            const result = await PartnerApi.CreateConsumserSettingsByPartnerId(partnerId);

            setState({ consumerSettings: result, generatingCredentials: false });

        } catch (error: any) {

            console.error('Failed to generate LTI credentials:', error);
            setState({ generatingCredentials: false });

        }

    };

    const saveBlackboardDeploymentId = async () => {

        try {

            setState({ savingBlackboardDeploymentId: true });

            const result = await PartnerApi.SaveManualLtiPlatformDeployment('blackboard', state.blackboardDeploymentId.trim());

            createToast(result.Message, result.Done ? 'success' : 'danger');

        } catch (error: any) {

            console.error('Failed to save the Blackboard Deployment ID:', error);
            createToast(formatMessage({ id: 'failed_to_save_deployment_id.77346cf5', defaultMessage: 'Failed to save Deployment ID.' }), 'danger');

        } finally {

            setState({ savingBlackboardDeploymentId: false });

        }

    };

    // Schoology installs GMetrix from the Schoology App Center rather than by manual tool
    // configuration, so — as with Blackboard — the only value the partner supplies is the
    // Deployment ID. No Client ID is sent: the App Center app is GMetrix-owned and its client_id is
    // resolved per environment server-side (DaoPartner.GetManualLtiLmsClientId, AppSetting
    // ManualLti_Schoology_ClientId).
    const saveSchoologyDeploymentId = async () => {

        try {

            setState({ savingSchoologyDeploymentId: true });

            const result = await PartnerApi.SaveManualLtiPlatformDeployment('schoology', state.schoologyDeploymentId.trim());

            createToast(result.Message, result.Done ? 'success' : 'danger');

        } catch (error: any) {

            console.error('Failed to save the Schoology Deployment ID:', error);
            createToast(formatMessage({ id: 'failed_to_save_deployment_id.77346cf5', defaultMessage: 'Failed to save Deployment ID.' }), 'danger');

        } finally {

            setState({ savingSchoologyDeploymentId: false });

        }

    };

    // Re-derives the four platform URLs from the entered site address. The derived values are only
    // defaults — the URL fields stay editable for setups the patterns don't cover (self-hosted Canvas, beta/test
    // environments, unusual Moodle routing). A field the user has manually changed away from the
    // previous address's derived value is preserved, so fixing a typo in the site address doesn't
    // wipe hand-entered corrections (e.g. the cloud-Canvas SSO endpoints).
    const applyManualPlatformUrl = (platformUrl: string) => {

        const lms = state.manualLms;

        const prev = buildManualPlatformEndpoints(lms, state.manualPlatformUrl);
        const next = buildManualPlatformEndpoints(lms, platformUrl);

        const rederive = (current: string, prevDerived: string | undefined, nextDerived: string | undefined) => (current && current !== (prevDerived ?? '') ? current : (nextDerived ?? ''));

        setState({
            manualAccessTokenUrl: rederive(state.manualAccessTokenUrl, prev?.accessTokenUrl, next?.accessTokenUrl),
            manualAuthorizeUrl: rederive(state.manualAuthorizeUrl, prev?.authorizeUrl, next?.authorizeUrl),
            manualIssuer: rederive(state.manualIssuer, prev?.issuer, next?.issuer),
            manualJwkSetUrl: rederive(state.manualJwkSetUrl, prev?.jwkSetUrl, next?.jwkSetUrl),
            manualPlatformUrl: platformUrl
        });

    };

    // Switching the LMS clears the whole form — site address, platform URLs, Client ID, and
    // Deployment ID all belong to the previous LMS, and carrying them over would silently register
    // the new LMS against the old LMS's host/credentials.
    const changeManualLms = (lms: ELms.CANVAS | ELms.MOODLE) => {

        setState({
            manualAccessTokenUrl: '',
            manualAuthorizeUrl: '',
            manualClientId: '',
            manualDeploymentId: '',
            manualIssuer: '',
            manualJwkSetUrl: '',
            manualLms: lms,
            manualPlatformUrl: ''
        });

    };

    // One save handler for the whole manual-registration form: a Canvas/Moodle registration
    // creates the partner's own platform from the collected endpoint URLs.
    const saveManualRegistration = async () => {

        try {

            setState({ savingManualPlatform: true });

            const result = await PartnerApi.ProvisionManualLtiPlatform(
                state.manualLms === ELms.CANVAS ? 'canvas' : 'moodle',
                state.manualClientId.trim(),
                state.manualDeploymentId.trim(),
                state.manualPlatformUrl.trim(),
                state.manualIssuer.trim(),
                state.manualAuthorizeUrl.trim(),
                state.manualAccessTokenUrl.trim(),
                state.manualJwkSetUrl.trim()
            );

            createToast(result.Message, result.Done ? 'success' : 'danger');

            // Clear the form on success so it's ready for the next registration (the LMS choice is
            // kept). The form never reloads saved entries — it always starts blank (see fetchSettings).
            if (result.Done) {

                setState({
                    manualAccessTokenUrl: '',
                    manualAuthorizeUrl: '',
                    manualClientId: '',
                    manualDeploymentId: '',
                    manualIssuer: '',
                    manualJwkSetUrl: '',
                    manualPlatformUrl: ''
                });

            }

        } catch (error: any) {

            console.error('Failed to register the LTI platform:', error);
            createToast(formatMessage({ id: 'failed_to_save_the_registration.93f3a021', defaultMessage: 'Failed to save the registration.' }), 'danger');

        } finally {

            setState({ savingManualPlatform: false });

        }

    };

    // The LTI 1.3 tool base URL is environment-provided; the dynamic-registration URL is base + identifier,
    // and the manual endpoints are the base + fixed route suffixes (kept in sync by buildLti13Endpoints).
    const advantageBase = state.consumerSettings?.AdvantageToolBaseURL ?? '';
    const lti13 = buildLti13Endpoints(advantageBase);
    const dynamicRegistrationURL = state.consumerSettings?.Identifier && advantageBase
        ? `${advantageBase}${state.consumerSettings.Identifier}`
        : '';

    // Blackboard registers GMetrix by a fixed, GMetrix-provided global Client ID, resolved
    // per-environment server-side (ConsumerSettings.BlackboardClientId); the hardcoded constant is
    // only the display default while settings load or fail.
    const blackboardClientId = state.consumerSettings?.BlackboardClientId || LMS_LTI13_CLIENT_IDS[ELms.BLACKBOARD] || '';

    const manualLmsLabel = LMS_LABELS[state.manualLms];

    // Which field the deep-linking URL goes into differs per LMS; empty for an LMS with no
    // documented content-selection field, in which case only the generic instruction is shown.
    const manualDeepLinkHint = state.manualLms === ELms.CANVAS
        ? formatMessage({ id: 'in_canvas_use_it_as_the_target_link_uri.c084a684', defaultMessage: 'In Canvas, use it as the target link URI on the tool’s assignment/link selection placement.' })
        : formatMessage({ id: 'in_moodle_paste_it_into_the_tool_configu.75d7b61b', defaultMessage: 'In Moodle, paste it into the tool configuration’s “Content Selection URL” field.' });

    // Both remaining manual LMSes collect the partner's own platform endpoints (site address + 4 URLs).
    const manualUrlsComplete = !!(state.manualIssuer.trim() && state.manualAuthorizeUrl.trim()
        && state.manualAccessTokenUrl.trim() && state.manualJwkSetUrl.trim());
    const manualFormComplete = !!(state.manualClientId.trim() && state.manualDeploymentId.trim()
        && state.manualPlatformUrl.trim() && manualUrlsComplete);

    const consumerKey = state.consumerSettings?.ConsumerKey ?? '';
    const consumerSecret = state.consumerSettings?.ConsumerSecret ?? '';
    const hasCredentials = consumerKey.length > 0 && consumerSecret.length > 0;

    // Custom parameters (gmxresourcekey + accesscode) that target a specific GMetrix resource without deep linking.
    const customParams = state.resourceKey.trim() ? buildCustomParams(state.resourceKey, state.accessCode) : [];
    const customParamsBlock = formatCustomParamsBlock(customParams);
    const resourceFieldValue = state.resourceKey.trim() ? `${RESOURCE_KEY_PARAM}=${state.resourceKey.trim()}` : '';
    const accessCodeFieldValue = state.accessCode.trim() ? `${ACCESS_CODE_PARAM}=${state.accessCode.trim()}` : '';

    return (
        <DefaultLayout pageTitle={formatMessage({ id: 'integrations.89572b90', defaultMessage: 'Integrations' })}>
            <div className="lti-integrations d-flex flex-column gap-3">
                <PageHeader helpContent={helpContent} title={formatMessage({ id: 'lti_integrations.8ed1dfc8', defaultMessage: 'LTI Integrations' })} />

                {state.loading ?
                    (
                        <div className="text-center p-4">
                            <FormattedMessage defaultMessage="Loading..." id="loading.2350c7f9" />
                        </div>
                    )
                    :
                    (
                        <>
                            {/* Intro */}
                            <Card body>
                                <h5 className="mb-2">
                                    <FormattedMessage defaultMessage="Add LTI Integration" id="add_lti_integration.da5c3b98" />
                                </h5>
                                <p className="text-muted mb-0">
                                    <FormattedMessage defaultMessage="Use this page to set up an LTI connection between GMetrix and your LMS. Start with the LTI 1.3 option that matches your LMS setup. Use LTI 1.1 only when LTI 1.3 is not supported by your LMS." id="use_this_page_to_set_up_an_lti_connectio.75b09228" />
                                </p>
                            </Card>

                            {/* Help banner */}
                            <div className="alert alert-primary d-flex flex-wrap align-items-center justify-content-between gap-3 mb-0">
                                <div className="d-flex align-items-start gap-3">
                                    <i className="fa-solid fa-lightbulb fa-lg mt-1" />
                                    <div>
                                        <div className="fw-bold">
                                            <FormattedMessage defaultMessage="Need help setting up your LMS connection?" id="need_help_setting_up_your_lms_connection.c7591227" />
                                        </div>
                                        <div className="small">
                                            <FormattedMessage defaultMessage="Our Knowledge Base has setup guidance and answers for each LMS" id="our_knowledge_base_has_setup_guidance_an.2ce8b798" />
                                        </div>
                                    </div>
                                </div>
                                <GMetrixButton onClick={() => window.open(GMETRIX_LTI_SETUP_OVERVIEW_URL, '_blank')} variant="primary">
                                    <FormattedMessage defaultMessage="View LTI Setup Overview" id="view_lti_setup_overview.549b87ba" />
                                    <i className="fa-solid fa-arrow-up-right-from-square ms-2 align-baseline" />
                                </GMetrixButton>
                            </div>

                            <Accordion defaultActiveKey="dynamic">

                                {/* 1. LTI 1.3 Dynamic Registration | Canvas, D2L Brightspace, Moodle */}
                                <Accordion.Item eventKey="dynamic">
                                    <Accordion.Header>
                                        <FormattedMessage defaultMessage="LTI 1.3 Dynamic Registration | Canvas, D2L Brightspace, Moodle" id="lti_13_dynamic_registration_or_canvas_d2.e86237ba" />
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        <p className="text-muted mb-3">
                                            <FormattedMessage defaultMessage="Use Dynamic Registration when your LMS supports it. Copy the URL below, then follow the setup steps for your LMS to register GMetrix." id="use_dynamic_registration_when_your_lms_s.ed5007fb" />
                                        </p>
                                        {dynamicRegistrationURL ?
                                            (
                                                <Row>
                                                    <Col xs={12}>
                                                        <CopyField label={formatMessage({ id: 'dynamic_registration_url.7a7f5fc6', defaultMessage: 'Dynamic Registration URL' })} onCopy={copyClipboardWithMessage} value={dynamicRegistrationURL} />
                                                    </Col>
                                                </Row>
                                            )
                                            :
                                            (
                                                <div>
                                                    <p className="text-muted small mb-2">
                                                        <FormattedMessage defaultMessage="A dynamic registration URL hasn't been generated for this testing center yet." id="a_dynamic_registration_url_hasnt_been_ge.9a947b18" />
                                                    </p>
                                                    {state.consumerSettings ?
                                                        (
                                                            <p className="text-muted small mb-0">
                                                                <FormattedMessage defaultMessage="Contact GMetrix support to enable LTI 1.3." id="contact_gmetrix_support_to_enable_lti_13.53bba214" />
                                                            </p>
                                                        )
                                                        :
                                                        (
                                                            <GMetrixButton disabled={state.generatingCredentials} onClick={generateLTIKeyAndSecret} variant="primary">
                                                                {state.generatingCredentials ? formatMessage({ id: 'generating.aea86213', defaultMessage: 'Generating…' }) : formatMessage({ id: 'generate_registration_url.a2f438d1', defaultMessage: 'Generate Registration URL' })}
                                                            </GMetrixButton>
                                                        )
                                                    }
                                                </div>
                                            )
                                        }
                                        <SetupStepsLinks lmsList={[ELms.CANVAS, ELms.D2L, ELms.MOODLE]} ltiVersion={ELtiVersion.LTI_13} />
                                    </Accordion.Body>
                                </Accordion.Item>

                                {/* 2. LTI 1.3 Client ID Registration | Blackboard */}
                                <Accordion.Item eventKey="blackboard">
                                    <Accordion.Header>
                                        <FormattedMessage defaultMessage="LTI 1.3 Client ID Registration | Blackboard" id="lti_13_client_id_registration_or_blackbo.4f398bec" />
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        <p className="text-muted mb-3">
                                            <FormattedMessage defaultMessage="Use this option for Blackboard LTI 1.3 setup. Copy the ID below to install GMetrix in Blackboard, then return here to enter the Deployment ID generated by Blackboard." id="use_this_option_for_blackboard_lti_13_se.0cfe0ce1" />
                                        </p>
                                        <Row className="mb-3">
                                            <Col xs={12}>
                                                <CopyField label={formatMessage({ id: 'client_id.e1d3f93f', defaultMessage: 'Client ID' })} onCopy={copyClipboardWithMessage} value={blackboardClientId} />
                                            </Col>
                                        </Row>
                                        <p className="mb-2">
                                            <FormattedMessage
                                                values={{ b: chunks => (
                                                    <span className="fw-bold">
                                                        {chunks}
                                                    </span>
                                                ) }} defaultMessage="<b>Finish Blackboard Setup:</b> after installing GMetrix in Blackboard, enter the Deployment ID it generates to link the deployment to this Testing Center."
                                                id="finish_blackboard_setup_after_installing.3a7add6f"
                                            />
                                        </p>
                                        <Row className="align-items-end g-3">
                                            <Col lg={6} md={8}>
                                                <Form.Group>
                                                    <Form.Label className="fw-bold">
                                                        <FormattedMessage defaultMessage="Deployment ID" id="deployment_id.8dde5d28" />
                                                    </Form.Label>
                                                    <Form.Control
                                                        aria-label={formatMessage({ id: 'deployment_id.8dde5d28', defaultMessage: 'Deployment ID' })}
                                                        onChange={e => setState({ blackboardDeploymentId: e.target.value })}
                                                        value={state.blackboardDeploymentId}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col xs="auto">
                                                <GMetrixButton
                                                    disabled={state.savingBlackboardDeploymentId || !state.blackboardDeploymentId.trim()}
                                                    iconClass="fa-solid fa-floppy-disk"
                                                    onClick={saveBlackboardDeploymentId}
                                                    variant="primary"
                                                >
                                                    <FormattedMessage defaultMessage="Save ID" id="save_id.7731a5d4" />
                                                </GMetrixButton>
                                            </Col>
                                        </Row>
                                        <SetupStepsLinks lmsList={[ELms.BLACKBOARD]} ltiVersion={ELtiVersion.LTI_13} />
                                    </Accordion.Body>
                                </Accordion.Item>

                                {/* 3. LTI 1.3 App Center Installation | Schoology — hidden while the App Center listing is in review (AB#41173) */}
                                {SHOW_SCHOOLOGY_APP_CENTER_PANEL && (
                                    <Accordion.Item eventKey="schoology">
                                        <Accordion.Header>
                                            <FormattedMessage defaultMessage="LTI 1.3 App Center Installation | Schoology" id="lti_13_app_center_installation_or_school.fe298e1f" />
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            <p className="text-muted mb-3">
                                                <FormattedMessage defaultMessage="Schoology doesn't support the manual LTI 1.3 tool configuration used by other LMSes. Install GMetrix from the Schoology App Center instead — the setup steps below walk through it — then return here to enter the Deployment ID Schoology generates." id="schoology_doesnt_support_the_manual_lti.3cbbe12a" />
                                            </p>
                                            <p className="mb-2">
                                                <FormattedMessage
                                                    values={{ b: chunks => (
                                                        <span className="fw-bold">
                                                            {chunks}
                                                        </span>
                                                    ) }} defaultMessage="<b>Finish Schoology Setup:</b> after installing GMetrix from the App Center, enter the Deployment ID it generates to link the deployment to this Testing Center. No tool URLs or Client ID are needed — the App Center app carries GMetrix's own configuration."
                                                    id="finish_schoology_setup_after_installing.16197598"
                                                />
                                            </p>
                                            <Row className="align-items-end g-3">
                                                <Col lg={6} md={8}>
                                                    <Form.Group>
                                                        <Form.Label className="fw-bold">
                                                            <FormattedMessage defaultMessage="Deployment ID" id="deployment_id.8dde5d28" />
                                                        </Form.Label>
                                                        <Form.Control
                                                            aria-label={formatMessage({ id: 'schoology_deployment_id.43c60811', defaultMessage: 'Schoology Deployment ID' })}
                                                            onChange={e => setState({ schoologyDeploymentId: e.target.value })}
                                                            value={state.schoologyDeploymentId}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col xs="auto">
                                                    <GMetrixButton
                                                        disabled={state.savingSchoologyDeploymentId || !state.schoologyDeploymentId.trim()}
                                                        iconClass="fa-solid fa-floppy-disk"
                                                        onClick={saveSchoologyDeploymentId}
                                                        variant="primary"
                                                    >
                                                        <FormattedMessage defaultMessage="Save ID" id="save_id.7731a5d4" />
                                                    </GMetrixButton>
                                                </Col>
                                            </Row>
                                            <SetupStepsLinks lmsList={[ELms.SCHOOLOGY]} ltiVersion={ELtiVersion.LTI_13} />
                                        </Accordion.Body>
                                    </Accordion.Item>
                                )}

                                {/* 4. LTI 1.3 Manual Registration | Canvas, Moodle */}
                                <Accordion.Item eventKey="manual">
                                    <Accordion.Header>
                                        <FormattedMessage defaultMessage="LTI 1.3 Manual Registration | Canvas, Moodle" id="lti_13_manual_registration_or_canvas_moo.2c98f8c5" />
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        <p className="text-muted mb-3">
                                            <FormattedMessage defaultMessage="Use this option when Dynamic Registration is not available for your LMS. Copy the GMetrix tool values below into your LMS, then return here to finish the registration." id="use_this_option_when_dynamic_registratio.ba25b1bf" />
                                        </p>
                                        <ToolEndpointFields endpoints={lti13} hasBaseUrl={!!advantageBase} onCopy={copyClipboardWithMessage} />
                                        {!!advantageBase && (
                                            <p className="text-muted small mt-2 mb-0">
                                                <FormattedMessage
                                                    values={{ b: chunks => (
                                                        <span className="fw-bold text-body">
                                                            {chunks}
                                                        </span>
                                                    ) }} defaultMessage="<b>Deep Linking (Content Selection) URL:</b> configure this one as well so instructors can choose a specific GMetrix practice test or course while adding the link. Without it your LMS can only create a generic GMetrix link, which opens the student's course list instead of the chosen resource."
                                                    id="deep_linking_content_selection_url_confi.3fb16bc1"
                                                />
                                                {manualDeepLinkHint ? ` ${manualDeepLinkHint}` : ''}
                                            </p>
                                        )}
                                        <p className="mb-2 mt-4">
                                            <FormattedMessage
                                                values={{ b: chunks => (
                                                    <span className="fw-bold">
                                                        {chunks}
                                                    </span>
                                                ) }} defaultMessage="<b>Your platform:</b> choose your LMS and enter your site address — the platform URLs below are prefilled automatically, and you can adjust any of them if your setup differs."
                                                id="your_platform_choose_your_lms_and_enter.7d09a270"
                                            />
                                        </p>
                                        <Row className="g-3">
                                            <Col lg={3} md={4}>
                                                <Form.Group>
                                                    <Form.Label className="fw-bold">
                                                        <FormattedMessage defaultMessage="LMS" id="lms.7488b1c7" />
                                                    </Form.Label>
                                                    <Form.Select
                                                        aria-label={formatMessage({ id: 'manual_registration_lms.489bd15a', defaultMessage: 'Manual registration LMS' })}
                                                        onChange={e => changeManualLms(e.target.value as ELms.CANVAS | ELms.MOODLE)}
                                                        value={state.manualLms}
                                                    >
                                                        <option value={ELms.CANVAS}>
                                                            {LMS_LABELS[ELms.CANVAS]}
                                                        </option>
                                                        <option value={ELms.MOODLE}>
                                                            {LMS_LABELS[ELms.MOODLE]}
                                                        </option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col lg={5} md={8}>
                                                <Form.Group>
                                                    <Form.Label className="fw-bold">
                                                        <FormattedMessage defaultMessage="Your {lms} URL" id="your_lms_url.f2985fd9" values={{ lms: manualLmsLabel }} />
                                                    </Form.Label>
                                                    <Form.Control
                                                        aria-label={formatMessage({ id: 'platform_site_address.bf5e9a8d', defaultMessage: 'Platform site address' })}
                                                        onChange={e => applyManualPlatformUrl(e.target.value)}
                                                        placeholder={state.manualLms === ELms.CANVAS ? 'myschool.instructure.com' : 'moodle.myschool.edu'}
                                                        value={state.manualPlatformUrl}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row className="g-3 mt-1">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-bold">
                                                        <FormattedMessage defaultMessage="Platform Issuer" id="platform_issuer.f7557340" />
                                                    </Form.Label>
                                                    <Form.Control
                                                        aria-label={formatMessage({ id: 'platform_issuer.ab7901a4', defaultMessage: 'Platform issuer' })}
                                                        onChange={e => setState({ manualIssuer: e.target.value })}
                                                        value={state.manualIssuer}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-bold">
                                                        <FormattedMessage defaultMessage="Platform Authorization URL" id="platform_authorization_url.65fa16bf" />
                                                    </Form.Label>
                                                    <Form.Control
                                                        aria-label={formatMessage({ id: 'platform_authorization_url.04d37b19', defaultMessage: 'Platform authorization URL' })}
                                                        onChange={e => setState({ manualAuthorizeUrl: e.target.value })}
                                                        value={state.manualAuthorizeUrl}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-bold">
                                                        <FormattedMessage defaultMessage="Platform Access Token URL" id="platform_access_token_url.87585742" />
                                                    </Form.Label>
                                                    <Form.Control
                                                        aria-label={formatMessage({ id: 'platform_access_token_url.b76c1468', defaultMessage: 'Platform access token URL' })}
                                                        onChange={e => setState({ manualAccessTokenUrl: e.target.value })}
                                                        value={state.manualAccessTokenUrl}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-bold">
                                                        <FormattedMessage defaultMessage="Platform Keyset (JWKS) URL" id="platform_keyset_jwks_url.94206a51" />
                                                    </Form.Label>
                                                    <Form.Control
                                                        aria-label={formatMessage({ id: 'platform_keyset_url.28184d63', defaultMessage: 'Platform keyset URL' })}
                                                        onChange={e => setState({ manualJwkSetUrl: e.target.value })}
                                                        value={state.manualJwkSetUrl}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        {state.manualLms === ELms.CANVAS && (
                                            <p className="text-muted small mt-2 mb-0">
                                                <FormattedMessage defaultMessage="The URLs above are built from your Canvas address (correct for self-hosted Canvas). For cloud-hosted Canvas (a *.instructure.com or vanity domain), replace them with Instructure's shared endpoints: issuer https://canvas.instructure.com, and https://sso.canvaslms.com/api/lti/authorize_redirect, /login/oauth2/token, and /api/lti/security/jwks." id="the_urls_above_are_built_from_your_canva.32f9d9c4" />
                                            </p>
                                        )}
                                        <p className="mb-2 mt-4">
                                            <FormattedMessage
                                                values={{
                                                    b: chunks => (
                                                        <span className="fw-bold">
                                                            {chunks}
                                                        </span>
                                                    ),
                                                    lms: manualLmsLabel
                                                }} defaultMessage="<b>Finish {lms} Setup:</b> after configuring GMetrix in {lms}, enter the Client ID and Deployment ID it generates to link the deployment to this Testing Center."
                                                id="finish_lms_setup_after_configuring_gmetr.706522c5"
                                            />
                                        </p>
                                        <Row className="align-items-end g-3">
                                            <Col lg={4} md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-bold">
                                                        <FormattedMessage defaultMessage="Client ID" id="client_id.e1d3f93f" />
                                                    </Form.Label>
                                                    <Form.Control
                                                        aria-label={formatMessage({ id: 'manual_registration_client_id.9f032601', defaultMessage: 'Manual registration Client ID' })}
                                                        onChange={e => setState({ manualClientId: e.target.value })}
                                                        value={state.manualClientId}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col lg={4} md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-bold">
                                                        <FormattedMessage defaultMessage="Deployment ID" id="deployment_id.8dde5d28" />
                                                    </Form.Label>
                                                    <Form.Control
                                                        aria-label={formatMessage({ id: 'manual_registration_deployment_id.82a2ad1d', defaultMessage: 'Manual registration Deployment ID' })}
                                                        onChange={e => setState({ manualDeploymentId: e.target.value })}
                                                        value={state.manualDeploymentId}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col xs="auto">
                                                <GMetrixButton
                                                    disabled={state.savingManualPlatform || !manualFormComplete}
                                                    iconClass="fa-solid fa-floppy-disk"
                                                    onClick={saveManualRegistration}
                                                    variant="primary"
                                                >
                                                    <FormattedMessage defaultMessage="Save Setup" id="save_setup.43b6c139" />
                                                </GMetrixButton>
                                            </Col>
                                        </Row>
                                        <SetupStepsLinks lmsList={[state.manualLms]} ltiVersion={ELtiVersion.LTI_13} />
                                    </Accordion.Body>
                                </Accordion.Item>

                                {/* 5. LTI 1.1 Legacy Setup */}
                                <Accordion.Item eventKey="legacy">
                                    <Accordion.Header>
                                        <FormattedMessage defaultMessage="LTI 1.1 Legacy Setup" id="lti_11_legacy_setup.77431d23" />
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        <p className="text-muted mb-3">
                                            <FormattedMessage defaultMessage="Use LTI 1.1 when LTI 1.3 is not supported by your LMS. Copy the values below and follow the setup steps for your LMS." id="use_lti_11_when_lti_13_is_not_supported.2d5a2ed9" />
                                        </p>
                                        {hasCredentials ?
                                            (
                                                <Row className="g-3">
                                                    <Col md={6}>
                                                        <CopyField label={formatMessage({ id: 'consumer_key.0bfc65df', defaultMessage: 'Consumer Key' })} onCopy={copyClipboardWithMessage} value={consumerKey} />
                                                    </Col>
                                                    <Col md={6}>
                                                        <CopyField label={formatMessage({ id: 'shared_secret.932767aa', defaultMessage: 'Shared Secret' })} onCopy={copyClipboardWithMessage} value={consumerSecret} />
                                                    </Col>
                                                    <Col md={6}>
                                                        <CopyField label={formatMessage({ id: 'launch_url.5a3b66af', defaultMessage: 'Launch URL' })} onCopy={copyClipboardWithMessage} value={GMETRIX_LTI_LAUNCH_URL} />
                                                    </Col>
                                                </Row>
                                            )
                                            :
                                            (
                                                <div>
                                                    <p className="text-muted small mb-2">
                                                        <FormattedMessage defaultMessage="No LTI 1.1 credentials have been generated yet. Generate a key and secret to connect your LMS to GMetrix." id="no_lti_11_credentials_have_been_generate.37ee6f26" />
                                                    </p>
                                                    <GMetrixButton disabled={state.generatingCredentials} onClick={generateLTIKeyAndSecret} variant="primary">
                                                        {state.generatingCredentials ? formatMessage({ id: 'generating.aea86213', defaultMessage: 'Generating…' }) : formatMessage({ id: 'request_lti_key_and_secret.42022008', defaultMessage: 'Request LTI Key and Secret' })}
                                                    </GMetrixButton>
                                                </div>
                                            )
                                        }

                                        {/* Link to a specific resource (custom parameters) */}
                                        <div className="mt-4 pt-3 border-top">
                                            <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
                                                <p className="text-muted small mb-0" style={{ maxWidth: 720 }}>
                                                    <FormattedMessage
                                                        values={{ b: chunks => (
                                                            <span className="fw-bold text-body">
                                                                {chunks}
                                                            </span>
                                                        ) }} defaultMessage="<b>Link to a specific resource:</b> to launch a specific GMetrix practice test or course without deep linking, choose the resource and access code below, then add the generated custom parameters to your LMS tool configuration."
                                                        id="link_to_a_specific_resource_to_launch_a.1a022da5"
                                                    />
                                                </p>
                                                <GMetrixButton
                                                    disabled={!state.resourceKey.trim() || !state.accessCode.trim()}
                                                    iconClass="fa-regular fa-copy"
                                                    onClick={() => copyClipboardWithMessage(customParamsBlock)}
                                                    variant="primary"
                                                >
                                                    <FormattedMessage defaultMessage="Copy All Parameters" id="copy_all_parameters.6f0eafd7" />
                                                </GMetrixButton>
                                            </div>

                                            <Row className="g-3">
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="fw-bold">
                                                            <FormattedMessage defaultMessage="Choose the resource to link:" id="choose_the_resource_to_link.87cd38e6" />
                                                        </Form.Label>
                                                        <InputGroup>
                                                            <Form.Control disabled aria-label={formatMessage({ id: 'resource_key.e108a607', defaultMessage: 'Resource key' })} placeholder={`${RESOURCE_KEY_PARAM}=`} value={resourceFieldValue} />
                                                            <GMetrixButton iconClass="fa-solid fa-magnifying-glass" onClick={() => setState({ showResourceModal: true })} variant="outline-primary">
                                                                <FormattedMessage defaultMessage="Look Up" id="look_up.71a81489" />
                                                            </GMetrixButton>
                                                        </InputGroup>
                                                    </Form.Group>
                                                    <Form.Group>
                                                        <Form.Label className="fw-bold">
                                                            <FormattedMessage defaultMessage="Choose the access code:" id="choose_the_access_code.ab7b4e5a" />
                                                        </Form.Label>
                                                        <InputGroup>
                                                            <Form.Control disabled aria-label={formatMessage({ id: 'access_code.e48b8c2c', defaultMessage: 'Access code' })} placeholder={`${ACCESS_CODE_PARAM}=`} value={accessCodeFieldValue} />
                                                            <GMetrixButton iconClass="fa-solid fa-magnifying-glass" onClick={() => setState({ showAccessCodeModal: true })} variant="outline-primary">
                                                                <FormattedMessage defaultMessage="Look Up" id="look_up.71a81489" />
                                                            </GMetrixButton>
                                                        </InputGroup>
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Control
                                                        disabled
                                                        aria-label={formatMessage({ id: 'generated_custom_parameters.1638461f', defaultMessage: 'Generated custom parameters' })}
                                                        as="textarea"
                                                        className="h-100"
                                                        placeholder={formatMessage({ id: 'choose_a_resource_to_generate_the_custom.60b6ad95', defaultMessage: 'Choose a resource to generate the custom parameters.' })}
                                                        rows={5}
                                                        value={customParamsBlock}
                                                    />
                                                    {state.resourceKey.trim() && !state.accessCode.trim() && (
                                                    <div className="text-danger small mt-1">
                                                        <FormattedMessage defaultMessage="An access code is required when linking a specific resource." id="an_access_code_is_required_when_linking.4d820daa" />
                                                    </div>
                                                    )}
                                                </Col>
                                            </Row>
                                        </div>

                                        <SetupStepsLinks lmsList={[ELms.BLACKBOARD, ELms.CANVAS, ELms.D2L, ELms.MOODLE, ELms.SCHOOLOGY]} ltiVersion={ELtiVersion.LTI_11} />
                                    </Accordion.Body>
                                </Accordion.Item>

                            </Accordion>

                            <ResourcePickerModal
                                onClose={() => setState({ showResourceModal: false })}
                                onSelect={value => setState({ resourceKey: value.deliverableControl, selectedResource: value })}
                                partnerId={partnerId}
                                selected={state.selectedResource}
                                show={state.showResourceModal}
                            />

                            <AccessCodeLookupModal
                                onClose={() => setState({ showAccessCodeModal: false })}
                                onSelect={value => setState({ accessCode: value })}
                                resourceKey={state.resourceKey}
                                show={state.showAccessCodeModal}
                            />
                        </>
                    )
                }
            </div>
        </DefaultLayout>
    );

};
